import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  StickyNote,
  Loader2,
  MoreVertical,
  Plus,
  Search,
  Star,
  Trash2,
  Copy,
} from 'lucide-react';
import {
  createNote,
  deleteNote,
  duplicateNote,
  listNotes,
  updateNote,
} from './services/notes.service.ts';
import type { Note, NoteFilter } from './types/note.types.ts';
import { authService } from '../../services/auth.service.ts';
import PageHeader, { pageHeaderPrimaryButtonClass } from '../dashboard/components/PageHeader.tsx';

function formatRelativeTime(timestamp: number): string {
  const diff = Date.now() - timestamp;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Az önce';
  if (minutes < 60) return `${minutes} dk önce`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} sa önce`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} gün önce`;
  return new Date(timestamp).toLocaleDateString('tr-TR');
}

function noteExcerpt(content: string, max = 140): string {
  const text = content.trim();
  if (!text) return 'Boş not';
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

const NotesPage: React.FC = () => {
  const navigate = useNavigate();
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<NoteFilter>('all');
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const userId = authService.getUser()?.id;

  const load = async () => {
    setLoading(true);
    try {
      setNotes(await listNotes());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const filtered = useMemo(() => {
    let list = [...notes];
    if (filter === 'owned' && userId) {
      list = list.filter((n) => n.ownerId === userId);
    }
    if (filter === 'recent') {
      const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
      list = list.filter((n) => n.updatedAt >= weekAgo);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (n) =>
          n.title.toLowerCase().includes(q) ||
          n.content.toLowerCase().includes(q)
      );
    }
    return list.sort((a, b) => {
      if (a.starred !== b.starred) return a.starred ? -1 : 1;
      return b.updatedAt - a.updatedAt;
    });
  }, [notes, filter, search, userId]);

  const handleCreate = async () => {
    const note = await createNote();
    navigate(`/products/notes/${note.id}`);
  };

  const handleToggleStar = async (note: Note, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await updateNote(note.id, { starred: !note.starred });
    await load();
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Bu notu silmek istediğinize emin misiniz?')) return;
    await deleteNote(id);
    setMenuOpenId(null);
    await load();
  };

  const handleDuplicate = async (id: string) => {
    const copy = await duplicateNote(id);
    setMenuOpenId(null);
    if (copy) navigate(`/products/notes/${copy.id}`);
  };

  const filters: Array<{ id: NoteFilter; label: string }> = [
    { id: 'all', label: 'Tüm notlar' },
    { id: 'owned', label: 'Sahip olduğum' },
    { id: 'recent', label: 'Son 7 gün' },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        icon={StickyNote}
        title="Notlar"
        description="Toplantı notlarınızı oluşturun, düzenleyin ve paylaşın."
        actions={
          <button type="button" onClick={() => void handleCreate()} className={pageHeaderPrimaryButtonClass}>
            <Plus className="w-5 h-5" />
            Yeni not
          </button>
        }
      />

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Not ara..."
            className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {filters.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                filter === f.id
                  ? 'bg-purple-600 text-white'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-gray-400 gap-3">
          <Loader2 className="w-5 h-5 animate-spin" />
          Yükleniyor...
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 bg-white/5 rounded-2xl border border-white/10 border-dashed">
          <StickyNote className="w-12 h-12 text-gray-500 mx-auto mb-4" />
          <h2 className="text-white font-semibold mb-2">Henüz not yok</h2>
          <p className="text-gray-400 text-sm mb-6 max-w-sm mx-auto">
            Toplantı sırasında veya sonrasında hızlıca not alın, düzenleyin ve paylaşın.
          </p>
          <button
            type="button"
            onClick={() => void handleCreate()}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-semibold"
          >
            <Plus className="w-5 h-5" />
            İlk notunuzu oluşturun
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((note) => (
            <div
              key={note.id}
              className="group relative bg-white/5 border border-white/10 rounded-xl overflow-hidden hover:border-purple-500/40 transition"
            >
              <Link to={`/products/notes/${note.id}`} className="block">
                <div className="h-28 p-4 bg-amber-50/95 relative overflow-hidden">
                  <p className="text-sm text-gray-700 line-clamp-4 whitespace-pre-wrap leading-relaxed">
                    {noteExcerpt(note.content, 200)}
                  </p>
                </div>
                <div className="p-4">
                  <h3 className="text-white font-medium truncate pr-8">{note.title}</h3>
                  <p className="text-gray-500 text-xs mt-1">
                    Düzenlendi: {formatRelativeTime(note.updatedAt)}
                  </p>
                </div>
              </Link>

              <button
                type="button"
                onClick={(e) => void handleToggleStar(note, e)}
                className={`absolute top-3 left-3 p-1.5 rounded-lg transition ${
                  note.starred
                    ? 'text-amber-400 bg-black/40'
                    : 'text-white/70 bg-black/30 opacity-0 group-hover:opacity-100'
                }`}
                title={note.starred ? 'Yıldızı kaldır' : 'Yıldızla'}
              >
                <Star className={`w-4 h-4 ${note.starred ? 'fill-current' : ''}`} />
              </button>

              <div className="absolute top-3 right-3">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setMenuOpenId(menuOpenId === note.id ? null : note.id);
                  }}
                  className="p-1.5 rounded-lg bg-black/30 text-white/80 hover:bg-black/50 opacity-0 group-hover:opacity-100 transition"
                >
                  <MoreVertical className="w-4 h-4" />
                </button>
                {menuOpenId === note.id && (
                  <div className="absolute right-0 mt-1 w-40 bg-slate-800 border border-white/10 rounded-lg shadow-xl z-10 py-1">
                    <button
                      type="button"
                      onClick={() => void handleDuplicate(note.id)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-200 hover:bg-white/10"
                    >
                      <Copy className="w-4 h-4" />
                      Kopyala
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleDelete(note.id)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-white/10"
                    >
                      <Trash2 className="w-4 h-4" />
                      Sil
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotesPage;
